/**
 * Unified Dashboard Component
 * Navigation flow: Projects → Processes → ProcessThreads → Designer
 * Includes full CRUD operations for all entities
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Project, Process, ProcessThread } from '@flow-studio/api';
import { ZapIcon, DocumentIcon, BizFirstIcon, PencilIcon, TrashIcon, ArrowLeftIcon } from '@bizfirst/common-react';
import { projectApiClient, processApiClient, processThreadApiClient } from '@flow-studio/api';
import { useUIStore } from '@flow-studio/store';
import { ProjectListView } from '@bizfirst/project-management-react';
import { Modal } from '../Support/Modal';
import { ConfirmDialog } from '../Support/ConfirmDialog';
import { ProjectForm } from './ProjectForm';
import { ProcessForm } from './ProcessForm';
import { ProcessThreadForm } from './ProcessThreadForm';
import { ProjectName } from './ProjectName';
import './unifiedDashboard.css';
import './processCards.css';

interface UnifiedDashboardProps {
  onProcessThreadSelect: (project: Project, process: Process, processThread: ProcessThread) => void;
  initialView?: DashboardView;
}

export type DashboardView =
  | { type: 'projects' }
  | { type: 'processes'; project: Project }
  | { type: 'threads'; project: Project; process: Process };

/** Project_ProjectTypes.ProjectTypeID 21 ("Workflow") — a Project whose Create action provisions
 *  a Flow Studio Process (Project_ProjectTypes.data.sql). App Studio's own Projects screen
 *  (ProjectTreePanel.tsx, `app-studio-designer-components-react`) reads this SAME
 *  `Project_Projects` table/`/api/v1/project/projects/*` family, so the two "All"/"Web Apps"
 *  filters mirror each other 1:1 — see that file's own doc comment for the full rationale
 *  (2026-09-05, Binoy). Here, the roles invert: this dashboard's own Projects ARE the Workflow
 *  Projects in the common case, and "Web Apps" is the tab that hides them down to whatever
 *  App-type/legacy Projects remain visible from this side too (a Project is shared state, not
 *  owned by either app).  */
const WORKFLOW_PROJECT_TYPE_ID = 21;

type ProjectViewFilter = 'all' | 'webApps' | 'workflowApps';

const PROJECT_VIEW_FILTER_TABS: Array<{ key: ProjectViewFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'webApps', label: 'Web Apps' },
  // Binoy 2026-09-14 (Task 20): mirrors App Studio's ProjectTreePanel.tsx 1:1 (see that file's
  // own comment on this same date) — explicit ProjectTypeID 21 ("Workflow") only, no
  // NULL-inclusion rule (that rule only applies to "Web Apps", to keep pre-existing untyped
  // Projects visible there).
  { key: 'workflowApps', label: 'Workflow Apps' },
];

const PAGE_SIZE_KEY = 'flow-studio-dashboard-page-size';
const PAGE_SIZE_OPTIONS = [12, 18, 24, 36];

const readStoredPageSize = (): number => {
  const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return PAGE_SIZE_OPTIONS.includes(stored) ? stored : 12;
};

export const UnifiedDashboard: React.FC<UnifiedDashboardProps> = ({
  onProcessThreadSelect,
  initialView,
}) => {
  const { theme, showToast } = useUIStore();

  const [view, setView] = useState<DashboardView>(initialView || { type: 'projects' });

  // All records (client-side pagination source)
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allProcesses, setAllProcesses] = useState<Process[]>([]);
  const [allThreads, setAllThreads] = useState<ProcessThread[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(readStoredPageSize);

  // "All" / "Web Apps" filter for the top-level Projects view — defaults to "All" (current
  // behavior) so this never silently hides a Project a user could already see.
  const [projectViewFilter, setProjectViewFilter] = useState<ProjectViewFilter>('all');
  const visibleProjects = useMemo(() => {
    if (projectViewFilter === 'webApps') return allProjects.filter(p => p.ProjectTypeID !== WORKFLOW_PROJECT_TYPE_ID);
    if (projectViewFilter === 'workflowApps') return allProjects.filter(p => p.ProjectTypeID === WORKFLOW_PROJECT_TYPE_ID);
    return allProjects;
  }, [allProjects, projectViewFilter]);

  // Derived totals from the full dataset
  const allItems = view.type === 'projects' ? allProjects
    : view.type === 'processes' ? allProcesses
    : allThreads;
  const totalCount = allItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Current page slice — projects paginate internally inside the shared ProjectListView now
  // (see renderContent's 'projects' branch), so only processes/threads still need a slice here.
  const pagedProcesses = useMemo(
    () => allProcesses.slice((page - 1) * pageSize, page * pageSize),
    [allProcesses, page, pageSize]
  );
  const pagedThreads = useMemo(
    () => allThreads.slice((page - 1) * pageSize, page * pageSize),
    [allThreads, page, pageSize]
  );

  // Modal state
  const [projectModal, setProjectModal] = useState<{ open: boolean; project?: Project }>({ open: false });
  const [processModal, setProcessModal] = useState<{ open: boolean; process?: Process }>({ open: false });
  const [threadModal, setThreadModal] = useState<{ open: boolean; thread?: ProcessThread }>({ open: false });

  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    type: 'project' | 'process' | 'thread' | null;
    id: number | null;
    name: string;
  }>({ open: false, type: null, id: null, name: '' });

  useEffect(() => {
    if (initialView) {
      setView(initialView);
      setPage(1);
    }
  }, [initialView]);

  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setPage(1);
    loadData(signal);
    return () => abortRef.current?.abort();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch all records — use PageInfo format the C# backend reads; pagination is client-side
  const ALL_RECORDS = { PageInfo: { PageNumber: 1, PageSize: 10000 } };

  const loadData = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      if (view.type === 'projects') {
        const response = await projectApiClient.getAll(ALL_RECORDS, { signal });
        if (signal?.aborted) return;
        if (response.success && response.data) {
          setAllProjects(response.data);
        } else {
          setError(response.error?.message || 'Failed to load projects');
        }
      } else if (view.type === 'processes') {
        const response = await processApiClient.getByProject(view.project.ProjectID, ALL_RECORDS, { signal });
        if (signal?.aborted) return;
        if (response.success && response.data) {
          setAllProcesses(response.data);
        } else {
          setError(response.error?.message || 'Failed to load processes');
        }
      } else if (view.type === 'threads') {
        const response = await processThreadApiClient.getByProcess(view.process.ProcessID, ALL_RECORDS, { signal });
        if (signal?.aborted) return;
        if (response.success && response.data) {
          setAllThreads(response.data);
        } else {
          setError(response.error?.message || 'Failed to load threads');
        }
      }
    } catch (err: any) {
      if (signal?.aborted || err.name === 'AbortError') return;
      if (err.message?.includes('fetch') || err.message?.includes('ERR_')) {
        setError('Cannot connect to server. Please check if the API server is running.');
      } else {
        setError(err.message || 'Failed to load data');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const handlePageChange = (p: number) => {
    setPage(Math.max(1, Math.min(p, totalPages)));
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
    localStorage.setItem(PAGE_SIZE_KEY, String(size));
  };

  const handleProjectClick = (project: Project) => {
    setPage(1);
    setView({ type: 'processes', project });
  };

  const handleProcessClick = (process: Process) => {
    setPage(1);
    setView({ type: 'threads', project: (view as any).project, process });
  };

  const handleThreadClick = (thread: ProcessThread) => {
    onProcessThreadSelect((view as any).project, (view as any).process, thread);
  };

  const handleBack = () => {
    setPage(1);
    if (view.type === 'threads') {
      setView({ type: 'processes', project: (view as any).project });
    } else if (view.type === 'processes') {
      setView({ type: 'projects' });
    }
  };

  // NOTE: these two handlers deliberately do NOT toggle the page-level `loading` state.
  // `loading` drives the *entire* list's render branch (ProjectListView's own loading
  // check, and renderBody() for processes/threads) — flipping it here replaced the whole
  // scrolled grid with a single-line "Loading..." placeholder for the split second this
  // lookup takes, which collapsed the scrollable container's content height and snapped
  // its scrollTop back to 0. Reproduced live: clicking "Design" on a card scrolled several
  // rows down instantly re-rendered the page scrolled to the top, so a user's very next
  // click (or their perception of where the click "landed") hit an entirely different card
  // near the top instead. `loading` stays reserved for the real list fetch (loadData());
  // this lookup is fast enough that no busy indicator is needed, and failures already
  // surface via showToast below.
  const handleProjectDesignClick = async (project: Project) => {
    try {
      const processesResponse = await processApiClient.getByProject(project.ProjectID);
      if (!processesResponse.success || !processesResponse.data?.length) {
        showToast('warning', 'No processes found for this project. Please create a process first.');
        return;
      }
      const firstProcess = processesResponse.data[0];
      const threadsResponse = await processThreadApiClient.getByProcess(firstProcess.ProcessID);
      if (!threadsResponse.success || !threadsResponse.data?.length) {
        showToast('warning', 'No process threads found. Please create a thread first.');
        return;
      }
      onProcessThreadSelect(project, firstProcess, threadsResponse.data[0]);
    } catch {
      showToast('error', 'Failed to open designer');
    }
  };

  const handleProcessDesignClick = async (process: Process) => {
    try {
      const threadsResponse = await processThreadApiClient.getByProcess(process.ProcessID);
      if (!threadsResponse.success || !threadsResponse.data?.length) {
        showToast('warning', 'No process threads found. Please create a thread first.');
        return;
      }
      onProcessThreadSelect((view as any).project, process, threadsResponse.data[0]);
    } catch {
      showToast('error', 'Failed to open designer');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.id || !deleteConfirm.type) return;
    try {
      if (deleteConfirm.type === 'project') {
        const response = await projectApiClient.deleteProject({ id: deleteConfirm.id });
        if (response.success) {
          showToast('success', `Project "${deleteConfirm.name}" deleted successfully!`);
          loadData();
        } else {
          showToast('error', response.error?.message || 'Failed to delete project');
        }
      } else if (deleteConfirm.type === 'process') {
        const response = await processApiClient.deleteProcess({ id: deleteConfirm.id });
        if (response.success) {
          showToast('success', `Process "${deleteConfirm.name}" deleted successfully!`);
          loadData();
        } else {
          showToast('error', response.error?.message || 'Failed to delete process');
        }
      } else if (deleteConfirm.type === 'thread') {
        const response = await processThreadApiClient.deleteProcessThread({ id: deleteConfirm.id });
        if (response.success) {
          showToast('success', `Process Thread "${deleteConfirm.name}" deleted successfully!`);
          loadData();
        } else {
          showToast('error', response.error?.message || 'Failed to delete process thread');
        }
      }
    } catch {
      showToast('error', 'An unexpected error occurred while deleting');
    }
  };

  // ── Styles ───────────────────────────────────────────────────────────────────

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    background: theme === 'dark' ? '#020617' : '#f9fafb',
    padding: '24px 40px 40px',
    overflowY: 'auto',
    boxSizing: 'border-box',
  };

  // The 'projects' view renders <ProjectListView>, which is a fully self-contained
  // container: it already brings its own width/height/padding/background/overflow (see
  // @bizfirst/project-management-react's ProjectListView.tsx). Wrapping it in the same
  // padded/backgrounded containerStyle above (as processes/threads do, since their content
  // is a bare grid with no container of its own) doubled the padding into a large gutter
  // and stacked two different background colors — ProjectListView's own container paints
  // its background via `var(--color-bg-primary, <fallback>)`, and that CSS variable is
  // globally defined (at #0f172a for dark mode) by atlas-forms-bridge's bundled
  // design-tokens.css under a bare `[data-theme="dark"]` selector, which this app's own
  // `data-theme` attribute also matches — so the fallback never won, and the resulting
  // panel was visibly lighter than the page's real #020617 background around it.
  // Fix: for 'projects' only, use this lean wrapper (no padding/background of its own —
  // ProjectListView supplies both) and reset the CSS custom properties ProjectListView
  // reads back to their guaranteed-invalid value so its own var(..., fallback) actually
  // applies, scoped to just this subtree (not touching the shared atlas-forms tokens or
  // any other page).
  const projectsWrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    // Column flex so the "All"/"Web Apps" tab row (fixed height) and ProjectListView (flex:1,
    // which supplies its own height:100%/overflowY:auto internally) stack and share this
    // wrapper's height properly instead of both independently claiming 100% of it.
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ['--color-bg-primary' as any]: 'initial',
    ['--color-bg-secondary' as any]: 'initial',
    ['--color-text-primary' as any]: 'initial',
    ['--color-text-tertiary' as any]: 'initial',
    ['--color-border' as any]: 'initial',
  };

  // Tab row above the Projects view (`view.type === 'projects'` only) — visual pattern matches
  // App Studio's ProjectTreePanel.tsx (same shared ProjectListView, same tab look), themed for
  // this app's own light/dark `theme` state.
  const projectViewFilterRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 2,
    flexShrink: 0,
    padding: '14px 40px 0',
    background: theme === 'dark' ? '#020617' : '#f9fafb',
    borderBottom: `1px solid ${theme === 'dark' ? '#1e293b' : '#e5e7eb'}`,
  };
  const projectViewFilterTabStyle: React.CSSProperties = {
    padding: '6px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: theme === 'dark' ? '#888' : '#6b7280',
    fontSize: '13px',
    cursor: 'pointer',
  };
  const projectViewFilterTabActiveStyle: React.CSSProperties = {
    color: theme === 'dark' ? '#e8e8f0' : '#111827',
    borderBottom: '2px solid #3A8C45',
  };
  const projectListViewAreaStyle: React.CSSProperties = { flex: 1, minHeight: 0, overflow: 'hidden' };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '24px',
    fontWeight: 700,
    color: theme === 'dark' ? '#f8fafc' : '#111827',
  };

  const createButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    background: theme === 'dark' ? 'rgba(34, 197, 94, 0.15)' : '#16a34a',
    color: '#ffffff',
    border: theme === 'dark' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid #16a34a',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s, border-color 0.15s',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridAutoRows: 'auto',
    gap: '24px',
  };

  const emptyColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const breadcrumbLinkStyle: React.CSSProperties = {
    cursor: 'pointer',
    transition: 'color 0.2s',
    color: theme === 'dark' ? '#3A8C45' : '#2d7038',
  };

  // ── Pagination bar — always dark palette ─────────────────────────────────────

  const shouldShowPagination = totalCount > 12;

  const paginationEl = (() => {
    const from = totalCount === 0 ? 0 : Math.min((page - 1) * pageSize + 1, totalCount);
    const to = Math.min(page * pageSize, totalCount);
    const label = view.type === 'projects' ? 'projects'
      : view.type === 'processes' ? 'processes' : 'threads';

    const btnStyle = (disabled: boolean): React.CSSProperties => ({
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 34, height: 34, borderRadius: 8,
      border: '1px solid rgba(34,197,94,0.25)',
      background: disabled ? 'rgba(51,65,85,0.25)' : 'rgba(34,197,94,0.10)',
      color: disabled ? '#475569' : '#4ade80',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 18, lineHeight: 1,
      transition: 'background 0.15s, color 0.15s',
      flexShrink: 0,
    });

    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginTop: 40,
        padding: '10px 16px', borderRadius: 10,
        background: 'rgba(15,23,42,0.7)',
        border: '1px solid rgba(34,197,94,0.15)',
        userSelect: 'none',
      }}>
        {/* Per page */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>Per page:</span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            style={{
              padding: '5px 10px', borderRadius: 8,
              border: '1px solid rgba(34,197,94,0.25)',
              background: '#0f172a',
              color: '#e2e8f0', fontSize: 13, cursor: 'pointer', outline: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
            } as React.CSSProperties}
          >
            {PAGE_SIZE_OPTIONS.map(opt => (
              <option key={opt} value={opt} style={{ background: '#0f172a', color: '#e2e8f0' }}>{opt}</option>
            ))}
          </select>
        </div>

        {/* Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            style={btnStyle(page <= 1)}
            disabled={page <= 1}
            onClick={() => handlePageChange(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          <span
            style={{
              fontSize: 13,
              color: '#94a3b8',
              minWidth: 180,
              textAlign: 'center',
            }}
          >
            {totalCount > 0
              ? `Showing ${from}–${to} of ${totalCount} ${label}`
              : `Page ${page} of ${totalPages}`}
          </span>

          <button
            style={btnStyle(page >= totalPages)}
            disabled={page >= totalPages}
            onClick={() => handlePageChange(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    );
  })();

  // ── Breadcrumb ───────────────────────────────────────────────────────────────

  const renderBreadcrumb = () => {
    const style: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '24px',
      fontSize: '14px',
      color: theme === 'dark' ? '#94a3b8' : '#64748b',
    };

    if (view.type === 'projects') return null;

    if (view.type === 'processes') {
      return (
        <div style={style}>
          <span
            style={breadcrumbLinkStyle}
            onClick={() => {
              setPage(1);
              setView({ type: 'projects' });
            }}
          >
            Projects
          </span>
          <span>→</span>
          <span>{view.project.Name}</span>
        </div>
      );
    }

    return (
      <div style={style}>
        <span
          style={breadcrumbLinkStyle}
          onClick={() => {
            setPage(1);
            setView({ type: 'projects' });
          }}
        >
          Projects
        </span>

        <span>→</span>

        <span
          style={breadcrumbLinkStyle}
          onClick={() => {
            setPage(1);
            setView({
              type: 'processes',
              project: view.project,
            });
          }}
        >
          {view.project.Name}
        </span>

        <span>→</span>
        <span>{view.process.Name}</span>
      </div>
    );
  };

  // ── Body wrapper (loading / error / content) ─────────────────────────────────

  const renderBody = (children: React.ReactNode) => {
    if (loading) {
      return (
        <div
          style={{
            textAlign: 'center',
            padding: '60px',
            color: emptyColor,
          }}
        >
          Loading...
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            textAlign: 'center',
            padding: '60px',
            backgroundColor: theme === 'dark' ? '#3f1f1f' : '#fee',
            border: `2px solid ${theme === 'dark' ? '#7f1f1f' : '#fcc'}`,
            borderRadius: '8px',
            color: theme === 'dark' ? '#fca5a5' : '#c33',
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: '12px' }}>
            Connection Error
          </h2>

          <p style={{ margin: '8px 0' }}>{error}</p>

          <button
            onClick={() => loadData()}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: theme === 'dark' ? '#7f1f1f' : '#fcc',
              color: theme === 'dark' ? '#fca5a5' : '#c33',
              border: `1px solid ${theme === 'dark' ? '#fca5a5' : '#c33'}`,
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            <ZapIcon size={14} /> Retry
          </button>
        </div>
      );
    }

    return <>{children}</>;
  };

  // ── Content ──────────────────────────────────────────────────────────────────

  const renderContent = () => {
    if (view.type === 'projects') {
      return (
        <>
          <div style={projectViewFilterRowStyle} role="tablist" aria-label="Project type filter">
            {PROJECT_VIEW_FILTER_TABS.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={projectViewFilter === t.key}
                style={{ ...projectViewFilterTabStyle, ...(projectViewFilter === t.key ? projectViewFilterTabActiveStyle : {}) }}
                onClick={() => setProjectViewFilter(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={projectListViewAreaStyle}>
            <ProjectListView
              subtitle="Build, connect, and manage your workflows."
              projects={visibleProjects}
              loading={loading}
              error={error}
              onRetry={() => loadData()}
              theme={theme}
              searchable
              searchPlaceholder="Search projects…"
              onCreateClick={() => setProjectModal({ open: true })}
              onDesignClick={project => handleProjectDesignClick(project)}
              onEditClick={project => setProjectModal({ open: true, project })}
              onDeleteClick={project =>
                setDeleteConfirm({ open: true, type: 'project', id: project.ProjectID, name: project.Name })
              }
              secondaryAction={{ label: 'Processes', onClick: project => handleProjectClick(project) }}
            />
          </div>
        </>
      );
    } else if (view.type === 'processes') return (
      <>
        <div style={headerStyle}>
          <div>
            <button onClick={handleBack} className="ud-btn--back"><ArrowLeftIcon size={16} /> Back</button>
            <h1 style={titleStyle}>Processes</h1>
          </div>
          <button style={createButtonStyle} onClick={() => setProcessModal({ open: true })}>
            + Create Process
          </button>
        </div>
        {renderBody(
          <>
            <div className="unified-dashboard__grid--narrow" style={gridStyle}>
              {pagedProcesses.map(process => (
                <EntityCard
                  key={process.ProcessID}
                  icon={process.IconUrl ? <BizFirstIcon icon={process.IconUrl} size={24} /> : <DocumentIcon size={24} />}
                  title={process.Name}
                  description={process.Description}
                  onClick={() => handleProcessDesignClick(process)}
                  onEdit={() => setProcessModal({ open: true, process })}
                  onDelete={() => setDeleteConfirm({ open: true, type: 'process', id: process.ProcessID, name: process.Name })}
                  actionButton={{ label: 'Design', onClick: () => handleProcessDesignClick(process) }}
                  navigationButton={{ label: 'Process Threads', onClick: () => handleProcessClick(process) }}
                />
              ))}
            </div>
            {allProcesses.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: emptyColor }}>
                No processes found. Create one to get started.
              </div>
            )}
            {shouldShowPagination && paginationEl}
          </>
        )}
      </>
    );

    return (
      <>
        <div style={headerStyle}>
          <div>
            <button onClick={handleBack} className="ud-btn--back"><ArrowLeftIcon size={16} /> Back</button>
            <h1 style={titleStyle}>Process Threads</h1>
          </div>
          <button style={createButtonStyle} onClick={() => setThreadModal({ open: true })}>
            + Create Thread
          </button>
        </div>
        {renderBody(
          <>
            <div className="unified-dashboard__grid--narrow" style={gridStyle}>
              {pagedThreads.map(thread => (
                <EntityCard
                  key={thread.ProcessThreadID}
                  icon={<DocumentIcon size={24} />}
                  title={thread.Name}
                  description={thread.Description}
                  onClick={() => handleThreadClick(thread)}
                  onEdit={() => setThreadModal({ open: true, thread })}
                  onDelete={() => setDeleteConfirm({ open: true, type: 'thread', id: thread.ProcessThreadID, name: thread.Name })}
                  actionButton={{ label: 'Design', onClick: () => handleThreadClick(thread) }}
                />
              ))}
            </div>
            {allThreads.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: emptyColor }}>
                No threads found. Create one to get started.
              </div>
            )}
            {shouldShowPagination && paginationEl}
          </>
        )}
      </>
    );
  };

  return (
    <div className="flow-dashboard" style={view.type === 'projects' ? projectsWrapperStyle : containerStyle}>
      {renderBreadcrumb()}
      {renderContent()}

      <Modal isOpen={projectModal.open} onClose={() => setProjectModal({ open: false })} title={projectModal.project ? 'Edit Project' : 'Create Project'}>
        <ProjectForm
          project={projectModal.project}
          onSuccess={() => { setProjectModal({ open: false }); loadData(); }}
          onCancel={() => setProjectModal({ open: false })}
          autoOpenDesigner={!projectModal.project}
          onOpenDesigner={(project, process, processThread) => {
            setProjectModal({ open: false });
            onProcessThreadSelect(project, process, processThread);
          }}
        />
      </Modal>

      <Modal isOpen={processModal.open} onClose={() => setProcessModal({ open: false })} title={processModal.process ? 'Edit Process' : 'Create Process'}>
        {view.type !== 'projects' && (
          <ProcessForm
            process={processModal.process}
            projectId={(view as any).project.ProjectID}
            onSuccess={() => { setProcessModal({ open: false }); loadData(); }}
            onCancel={() => setProcessModal({ open: false })}
          />
        )}
      </Modal>

      <Modal isOpen={threadModal.open} onClose={() => setThreadModal({ open: false })} title={threadModal.thread ? 'Edit Thread' : 'Create Thread'}>
        {view.type === 'threads' && (
          <ProcessThreadForm
            processThread={threadModal.thread}
            processId={view.process.ProcessID}
            onSuccess={() => { setThreadModal({ open: false }); loadData(); }}
            onCancel={() => setThreadModal({ open: false })}
          />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: null, id: null, name: '' })}
        onConfirm={handleDelete}
        title={`Delete ${deleteConfirm.type}?`}
        message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

// ── Entity Card ──────────────────────────────────────────────────────────────

interface EntityCardProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  color?: string;
  isActive?: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  actionButton?: { label: string; onClick: () => void };
  navigationButton?: { label: string; onClick: () => void };
}

const EntityCard: React.FC<EntityCardProps> = ({
  icon, title, description, color, isActive,
  onClick, onEdit, onDelete, actionButton, navigationButton,
}) => {
  const [hovered, setHovered] = useState(false);
  const cardColor = color || '#3A8C45';

  return (
    <div
      className="ud-card"
      style={{
        borderColor: hovered ? cardColor : cardColor + '66',
        boxShadow: hovered ? `0 4px 20px ${cardColor}33` : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <div className="ud-card__header">
        <span className="ud-card__icon">{icon}</span>
        <div className="ud-card__header-right">
          {isActive && <span className="ud-card__badge">Active</span>}
          {navigationButton && (
            <button className="ud-card__nav-label" onClick={(e) => { e.stopPropagation(); navigationButton.onClick(); }}>
              {navigationButton.label}
            </button>
          )}
        </div>
      </div>
      <div className="ud-card__title">
        <ProjectName name={title} />
      </div>
      {description && <p className="ud-card__description">{description}</p>}
      <div className="ud-card__spacer" />
      <div className="ud-card__actions" onClick={(e) => e.stopPropagation()}>
        {actionButton && (
          <>
            <button className="ud-btn--action" onClick={(e) => { e.stopPropagation(); actionButton.onClick(); }}>
              <ZapIcon size={12} /> {actionButton.label}
            </button>
            <span className="ud-btn__sep">|</span>
          </>
        )}
        <button className="ud-btn--action" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
          <PencilIcon size={12} /> Edit
        </button>
        <span className="ud-btn__sep">|</span>
        <button className="ud-btn--danger" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <TrashIcon size={12} /> Delete
        </button>
      </div>
    </div>
  );
};
